import { useCallback, useMemo, useState } from 'react'

import type {
  GridSelection,
  Row,
  TimeGridEntity,
  TimeGridEvent,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import {
  fromCellKey,
  getColumnCellCoords,
  getRowCellCoords,
  toCellKey,
} from 'modules/shift_management/utils/TimeGridTable/cellCoordinates'

interface UseGridSelectionProps<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent> {
  rows: Row<TEntity, TEvent>[]
  slots: TimeSlot[]
  rowIndexByEntityId: Map<string, number>
}

function useGridSelection<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent>({
  rows,
  slots,
  rowIndexByEntityId,
}: UseGridSelectionProps<TEntity, TEvent>): {
  selection: GridSelection
  selectedCellKeys: Set<string>
  selectedRowId: string | null
  selectedColumnIndex: number | null
  selectedFocusedCell: { entityId: string; slotIndex: number } | null
  selectedEventKeys: Set<string>
  clearSelection: () => void
  handleCellSelected: (
    entity: TEntity,
    slotIndex: number,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  ) => void
  handleRowHeaderSelected: (entity: TEntity) => void
  handleColumnHeaderSelected: (columnIndex: number) => void
  handleEventSelected: (
    entityId: string,
    eventId: string,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  ) => void
  handleCornerCellSelected: () => void
  isAllSelected: boolean
  fromCellKey: (key: string) => { entityId: string; slotIndex: number }
} {
  const [selection, setSelection] = useState<GridSelection>({ kind: 'none' })

  const checkSelectable = useCallback(
    (entityId: string): boolean => {
      const rowIndex = rowIndexByEntityId.get(entityId)
      if (rowIndex === undefined) return false
      const row = rows[rowIndex]
      return row?.entity.selectable !== false
    },
    [rows, rowIndexByEntityId]
  )

  const selectedCellKeys = useMemo(() => {
    return selection.kind === 'cells' ? new Set(selection.keys) : new Set<string>()
  }, [selection])

  const selectedRowId = selection.kind === 'row' ? selection.rowId : null
  const selectedColumnIndex = selection.kind === 'column' ? selection.columnIndex : null

  const selectedFocusedCell = useMemo(() => {
    if (selection.kind !== 'cells') return null
    return selection.focused
  }, [selection])

  const clearSelection = useCallback(() => {
    setSelection({ kind: 'none' })
  }, [])

  const selectColumn = useCallback((columnIndex: number) => {
    setSelection({ kind: 'column', columnIndex })
  }, [])

  const selectColumnCellsExcept = useCallback(
    (columnIndex: number, excludedRowId: string) => {
      const cells = getColumnCellCoords(columnIndex, rows).filter(
        (cell) => cell.entityId !== excludedRowId && checkSelectable(cell.entityId)
      )
      const next = new Set(cells.map((c) => toCellKey(c.entityId, c.slotIndex)))

      if (next.size === 0) {
        setSelection({ kind: 'none' })
        return
      }

      const fallbackRow = rows[0]?.entity.id
      const focusedRowId =
        fallbackRow && fallbackRow !== excludedRowId ? fallbackRow : rows[1]?.entity.id
      const firstSelectedKey = Array.from(next)[0]
      if (!firstSelectedKey) {
        setSelection({ kind: 'none' })
        return
      }
      const focusedKey = focusedRowId ? toCellKey(focusedRowId, columnIndex) : firstSelectedKey
      const focused = fromCellKey(focusedKey)
      setSelection({ kind: 'cells', keys: next, anchor: focused, focused })
    },
    [rows, checkSelectable]
  )

  const selectSingleCell = useCallback((cell: { entityId: string; slotIndex: number }) => {
    const key = toCellKey(cell.entityId, cell.slotIndex)
    setSelection({ kind: 'cells', keys: new Set([key]), anchor: cell, focused: cell })
  }, [])

  const toggleCell = useCallback((cell: { entityId: string; slotIndex: number }) => {
    const key = toCellKey(cell.entityId, cell.slotIndex)
    setSelection((prev) => {
      if (prev.kind !== 'cells') {
        return { kind: 'cells', keys: new Set([key]), anchor: cell, focused: cell }
      }

      const next = new Set(prev.keys)
      const wasSelected = next.has(key)
      // Meta-click toggles the clicked cell. If it was already selected, remove it.
      if (wasSelected) {
        next.delete(key)
      } else {
        next.add(key)
      }

      if (next.size === 0) {
        return { kind: 'none' }
      }
      const focused = cell
      let anchor = prev.anchor

      const anchorKey = toCellKey(anchor.entityId, anchor.slotIndex)

      // Keep anchor inside the selection; clipboard offsets rely on this invariant.
      if (!next.has(anchorKey)) {
        const focusedKey = toCellKey(focused.entityId, focused.slotIndex)

        const fallbackKey = next.has(focusedKey) ? focusedKey : Array.from(next)[0]

        if (!fallbackKey) {
          // should be unreachable because keys.size > 0
          return { kind: 'none' }
        }
        anchor = fromCellKey(fallbackKey)
      }

      return {
        kind: 'cells',
        keys: next,
        anchor,
        focused,
      }
    })
  }, [])

  const addRangeSameRow = useCallback(
    (rowId: string, startIndex: number, endIndex: number, append: boolean, focusedSlot: number) => {
      const minIndex = Math.min(startIndex, endIndex)
      const maxIndex = Math.max(startIndex, endIndex)
      setSelection((prev) => {
        const next = append && prev.kind === 'cells' ? new Set(prev.keys) : new Set<string>()
        for (let i = minIndex; i <= maxIndex; i += 1) {
          if (!checkSelectable(rowId)) continue
          next.add(toCellKey(rowId, i))
        }
        if (next.size === 0) return { kind: 'none' }
        const focused = { entityId: rowId, slotIndex: focusedSlot }
        const anchor = prev.kind === 'cells' ? prev.anchor : focused
        return { kind: 'cells', keys: next, anchor, focused }
      })
    },
    [checkSelectable]
  )

  const addRangeSameColumn = useCallback(
    (
      columnIndex: number,
      startRowId: string,
      endRowId: string,
      append: boolean,
      focusedRowId: string
    ) => {
      const startIndex = rowIndexByEntityId.get(startRowId)
      const endIndex = rowIndexByEntityId.get(endRowId)
      if (startIndex === undefined || endIndex === undefined) return

      const minIndex = Math.min(startIndex, endIndex)
      const maxIndex = Math.max(startIndex, endIndex)
      setSelection((prev) => {
        const next = append && prev.kind === 'cells' ? new Set(prev.keys) : new Set<string>()
        for (let i = minIndex; i <= maxIndex; i += 1) {
          const row = rows[i]
          if (!row) continue
          if (!checkSelectable(row.entity.id)) continue
          next.add(toCellKey(row.entity.id, columnIndex))
        }
        if (next.size === 0) return { kind: 'none' }
        const focused = { entityId: focusedRowId, slotIndex: columnIndex }
        const anchor = prev.kind === 'cells' ? prev.anchor : focused
        return { kind: 'cells', keys: next, anchor, focused }
      })
    },
    [rowIndexByEntityId, rows, checkSelectable]
  )

  const selectRowCellsExcept = useCallback(
    (rowId: string, excludedSlotIndex: number) => {
      const cells = getRowCellCoords(rowId, slots.length).filter(
        (cell) => cell.slotIndex !== excludedSlotIndex && checkSelectable(cell.entityId)
      )
      const next = new Set(cells.map((c) => toCellKey(c.entityId, c.slotIndex)))

      if (next.size === 0) {
        setSelection({ kind: 'none' })
        return
      }

      const fallbackSlot = excludedSlotIndex === 0 ? 1 : 0
      const firstSelectedKey = Array.from(next)[0]
      if (!firstSelectedKey) {
        setSelection({ kind: 'none' })
        return
      }
      const focusedKey = next.has(toCellKey(rowId, fallbackSlot))
        ? toCellKey(rowId, fallbackSlot)
        : firstSelectedKey
      const focused = fromCellKey(focusedKey)
      setSelection({ kind: 'cells', keys: next, anchor: focused, focused })
    },
    [slots.length, checkSelectable]
  )

  const handleCellSelected = useCallback(
    (
      entity: TEntity,
      slotIndex: number,
      modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
    ) => {
      if (!checkSelectable(entity.id)) return

      const cell = { entityId: entity.id, slotIndex }
      const isMetaPressed = modifiers.metaKey || modifiers.ctrlKey
      const anchorCell = selection.kind === 'cells' ? selection.anchor : null

      if (modifiers.shiftKey && !anchorCell) {
        selectSingleCell(cell)
        return
      }

      if (modifiers.shiftKey && anchorCell) {
        const sameRow = anchorCell.entityId === entity.id
        const sameColumn = anchorCell.slotIndex === slotIndex
        if (sameRow) {
          addRangeSameRow(entity.id, anchorCell.slotIndex, slotIndex, isMetaPressed, slotIndex)
          return
        }
        if (sameColumn) {
          addRangeSameColumn(slotIndex, anchorCell.entityId, entity.id, isMetaPressed, entity.id)
          return
        }
        selectSingleCell(cell)
        return
      }

      if (isMetaPressed && selectedRowId === entity.id) {
        selectRowCellsExcept(entity.id, slotIndex)
        return
      }

      if (isMetaPressed && selectedColumnIndex === slotIndex) {
        selectColumnCellsExcept(slotIndex, entity.id)
        return
      }

      if (isMetaPressed) {
        toggleCell(cell)
        return
      }

      if (selection.kind === 'cells' && selection.keys.size === 1) {
        const key = toCellKey(entity.id, slotIndex)
        if (selection.keys.has(key)) {
          clearSelection()
          return
        }
      }

      selectSingleCell(cell)
    },
    [
      addRangeSameColumn,
      addRangeSameRow,
      checkSelectable,
      clearSelection,
      selectSingleCell,
      selection,
      selectedColumnIndex,
      selectedRowId,
      selectRowCellsExcept,
      selectColumnCellsExcept,
      toggleCell,
    ]
  )

  const handleRowHeaderSelected = useCallback(
    (entity: TEntity) => {
      if (selectedRowId === entity.id) {
        clearSelection()
        return
      }

      if (!entity.selectable) return

      setSelection({ kind: 'row', rowId: entity.id })
    },
    [clearSelection, selectedRowId]
  )

  const handleColumnHeaderSelected = useCallback(
    (columnIndex: number) => {
      if (selectedColumnIndex === columnIndex) {
        clearSelection()
        return
      }

      const selectableEntityIds: string[] = []
      for (const row of rows) {
        if (checkSelectable(row.entity.id)) {
          selectableEntityIds.push(row.entity.id)
        }
      }

      if (selectableEntityIds.length === 0) return

      if (selectableEntityIds.length === rows.length) {
        selectColumn(columnIndex)
        return
      }

      const keys = new Set(selectableEntityIds.map((id) => toCellKey(id, columnIndex)))
      const firstEntityId = selectableEntityIds[0]
      if (!firstEntityId) return
      const anchor = { entityId: firstEntityId, slotIndex: columnIndex }

      setSelection((prev) => {
        if (
          prev.kind === 'cells' &&
          prev.keys.size === keys.size &&
          [...keys].every((k) => prev.keys.has(k))
        ) {
          return { kind: 'none' }
        }
        return { kind: 'cells', keys, anchor, focused: anchor }
      })
    },
    [clearSelection, selectColumn, selectedColumnIndex, checkSelectable, rows]
  )

  const toEventKey = useCallback((entityId: string, eventId: string) => {
    return `${entityId}:${eventId}`
  }, [])

  const selectedEventKeys = useMemo(() => {
    return selection.kind === 'event' ? new Set(selection.keys) : new Set<string>()
  }, [selection])

  const handleEventSelected = useCallback(
    (
      entityId: string,
      eventId: string,
      modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
    ) => {
      if (!checkSelectable(entityId)) return

      const key = toEventKey(entityId, eventId)
      const isMetaPressed = modifiers.metaKey || modifiers.ctrlKey

      if (isMetaPressed) {
        setSelection((prev) => {
          const next = prev.kind === 'event' ? new Set(prev.keys) : new Set<string>()

          if (next.has(key)) {
            next.delete(key)
          } else {
            next.add(key)
          }

          if (next.size === 0) {
            return { kind: 'none' }
          }

          return { kind: 'event', keys: next }
        })
        return
      }
      setSelection((prev) => {
        if (prev.kind === 'event' && prev.keys.size === 1 && prev.keys.has(key)) {
          return { kind: 'none' }
        }

        return { kind: 'event', keys: new Set([key]) }
      })
    },
    [checkSelectable, toEventKey]
  )

  const hasSelectableRows = useMemo(() => rows.some((r) => r.entity.selectable !== false), [rows])

  const canSelectAll = hasSelectableRows && slots.length > 0

  const isAllSelected = selection.kind === 'all'

  const handleCornerCellSelected = useCallback(() => {
    setSelection((prev) => {
      if (!canSelectAll) return { kind: 'none' }
      return prev.kind === 'all' ? { kind: 'none' } : { kind: 'all' }
    })
  }, [canSelectAll])

  return {
    selection,
    selectedCellKeys,
    selectedRowId,
    selectedColumnIndex,
    selectedFocusedCell,
    selectedEventKeys,
    clearSelection,
    handleCellSelected,
    handleRowHeaderSelected,
    handleColumnHeaderSelected,
    handleCornerCellSelected,
    isAllSelected,
    handleEventSelected,
    fromCellKey,
  }
}

export { useGridSelection }
